import React, {useEffect, useState} from 'react';
import {
    Show,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRedirect,
    useTranslate,
} from 'react-admin';
import {Avatar, Box, Link as MuiLink, Typography} from '@mui/material';
import {EditOutlined, OpenInNew} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatamicTabs, {useTabs} from '../components/statamic/StatamicTabs';
import {formatDateTime} from '../lib/dates';

const TIMEZONE_MAP = {
    'Europe/Amsterdam': 'Amsterdam (CET)',
    'Europe/London': 'London (GMT)',
    'Europe/Paris': 'Paris (CET)',
    'Europe/Berlin': 'Berlin (CET)',
    'America/New_York': 'New York (EST)',
    'America/Los_Angeles': 'Los Angeles (PST)',
    'Asia/Dubai': 'Dubai (GST)',
    'Asia/Singapore': 'Singapore (SGT)',
};

const CURRENCY_MAP = {
    EUR: '€ Euro (EUR)',
    GBP: '£ British Pound (GBP)',
    USD: '$ US Dollar (USD)',
    CHF: 'Swiss Franc (CHF)',
    SEK: 'Swedish Krona (SEK)',
    NOK: 'Norwegian Krone (NOK)',
    DKK: 'Danish Krone (DKK)',
    AED: 'UAE Dirham (AED)',
};

const LOCALE_MAP = {
    nl_NL: 'Nederlands (Nederland)',
    en_GB: 'English (United Kingdom)',
    en_US: 'English (United States)',
    de_DE: 'Deutsch (Deutschland)',
    fr_FR: 'Français (France)',
    es_ES: 'Español (España)',
    pt_PT: 'Português (Portugal)',
    it_IT: 'Italiano (Italia)',
    ar_AE: 'العربية (الإمارات)',
};

const BooleanCell = ({value, trueLabel = 'Yes', falseLabel = 'No'}) => (
    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 1}}>
        <StatusDot variant={value ? 'success' : 'neutral'}/>
        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
            {value ? trueLabel : falseLabel}
        </Typography>
    </Box>
);

const ExternalLink = ({href, children}) => (
    <MuiLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: t.color.accent.primary,
            fontSize: `${t.type.body.size}px`,
            textDecoration: 'none',
            wordBreak: 'break-all',
            '&:hover': { textDecoration: 'underline' },
        }}
    >
        {children}
        <OpenInNew sx={{fontSize: 13, flexShrink: 0}}/>
    </MuiLink>
);

const MetaRow = ({label, children}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'baseline', gap: 1.5, py: 0.5}}>
        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500}}>
            {label}
        </Typography>
        <Box sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, minWidth: 0}}>
            {children || <Box component="span" sx={{color: t.color.text.muted}}>—</Box>}
        </Box>
    </Box>
);

const UmbrellaHero = ({record, logoUrl}) => {
    const locationText = [record.city, record.country].filter(Boolean).join(', ') || null;

    return (
        <Box
            sx={{
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                p: 3,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                alignItems: 'stretch',
            }}
        >
            <Box sx={{display: 'flex', gap: 3, alignItems: 'center', minWidth: 0}}>
                <Avatar
                    src={logoUrl}
                    variant="rounded"
                    sx={{
                        width: 96,
                        height: 96,
                        fontSize: 34,
                        fontWeight: 600,
                        flexShrink: 0,
                        bgcolor: t.color.sidebarHover,
                        color: t.color.text.secondary,
                        border: `1px solid ${t.color.border.hairline}`,
                    }}
                >
                    {record.charity_name?.charAt(0)}
                </Avatar>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: t.color.text.primary,
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                        }}
                    >
                        {record.charity_name || 'Unnamed'}
                    </Typography>
                    {locationText && (
                        <Typography sx={{mt: 0.5, fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                            {locationText}
                        </Typography>
                    )}
                    <Box sx={{display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap'}}>
                        <StatusPill variant={record.active ? 'success' : 'neutral'}>
                            {record.active ? 'Active' : 'Inactive'}
                        </StatusPill>
                        {record.demo_charity && <StatusPill variant="info">Demo</StatusPill>}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pl: 3,
                    borderLeft: `1px solid ${t.color.border.hairline}`,
                }}
            >
                <MetaRow label="Email">
                    {record.email ? (
                        <MuiLink
                            href={`mailto:${record.email}`}
                            sx={{color: t.color.accent.primary, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                        >
                            {record.email}
                        </MuiLink>
                    ) : null}
                </MetaRow>
                <MetaRow label="Timezone">
                    {TIMEZONE_MAP[record.default_timezone] ?? record.default_timezone}
                </MetaRow>
                <MetaRow label="Currency">
                    {CURRENCY_MAP[record.default_currency] ?? record.default_currency}
                </MetaRow>
                <MetaRow label="Default language">
                    {LOCALE_MAP[record.default_locale] ?? record.default_locale}
                </MetaRow>
            </Box>
        </Box>
    );
};

const DetailsTab = ({record, logoUrl}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        <UmbrellaHero record={record} logoUrl={logoUrl}/>

        {record.description && (
            <SectionCard title="Description">
                <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, whiteSpace: 'pre-wrap'}}>
                    {record.description}
                </Typography>
            </SectionCard>
        )}

        <SectionCard title="Payment">
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                <DataRow label="Stripe payment link">
                    {record.payment_link_url
                        ? <ExternalLink href={record.payment_link_url}>{record.payment_link_url}</ExternalLink>
                        : null}
                </DataRow>
                <DataRow label="PayPal payment link">
                    {record.paypal_payment_link_url
                        ? <ExternalLink href={record.paypal_payment_link_url}>{record.paypal_payment_link_url}</ExternalLink>
                        : null}
                </DataRow>
            </Box>
        </SectionCard>

        <SectionCard title="Messaging & notifications">
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: 3, rowGap: 0}}>
                <BooleanCell value={record.allow_text_messages}  trueLabel="Text messages allowed"  falseLabel="Text messages disabled"/>
                <BooleanCell value={record.allow_photo_messages} trueLabel="Photo messages allowed" falseLabel="Photo messages disabled"/>
                <BooleanCell value={record.allow_video_messages} trueLabel="Video messages allowed" falseLabel="Video messages disabled"/>
                <BooleanCell value={record.send_new_post_mail}   trueLabel="New-post e-mail enabled" falseLabel="New-post e-mail disabled"/>
                <BooleanCell value={record.require_post_approval} trueLabel="Post approval required" falseLabel="No post approval"/>
                <BooleanCell value={record.send_registration_copy_to_projects} trueLabel="Registration notice to projects" falseLabel="No registration notice to projects"/>
            </Box>
        </SectionCard>
    </Box>
);

const CharitiesTab = ({record}) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const [charities, setCharities] = useState([]);
    const [loading, setLoading] = useState(true);
    // The v2 charities filter matches umbrella_id exactly as a UUID — a
    // legacy numeric-routed record (v1 fallback) has no UUID to scope by,
    // and the provider strips a non-UUID umbrella_id filter, which would
    // otherwise silently list ALL charities here instead of this umbrella's.
    const isUuidScoped = typeof record?.id === 'string' && record.id.includes('-');

    useEffect(() => {
        if (!record?.id || !isUuidScoped) return;
        setLoading(true);
        dataProvider.getList('charities', {
            pagination: {page: 1, perPage: 100},
            sort: {field: 'charity_name', order: 'ASC'},
            filter: {umbrella_id: record.id},
        })
            .then(({data}) => { setCharities(data); setLoading(false); })
            .catch(() => { notify('Error loading member charities', {type: 'error'}); setLoading(false); });
    }, [record, isUuidScoped, dataProvider, notify]);

    if (!isUuidScoped) {
        return (
            <SectionCard title="Member charities">
                <Typography sx={{py: 3, color: t.color.text.muted, fontSize: `${t.type.small.size}px`, textAlign: 'center'}}>
                    {translate('umbrella.charities_tab_unscoped_notice', {_: 'Open this umbrella from the umbrellas list to see its charities'})}
                </Typography>
            </SectionCard>
        );
    }

    return (
        <SectionCard title="Member charities" subtitle={`${charities.length} organization${charities.length === 1 ? '' : 's'} under this umbrella`}>
            {loading && (
                <Typography sx={{py: 3, color: t.color.text.muted, fontSize: `${t.type.small.size}px`, textAlign: 'center'}}>
                    Loading…
                </Typography>
            )}
            {!loading && charities.length === 0 && (
                <Typography sx={{py: 3, color: t.color.text.muted, fontSize: `${t.type.small.size}px`, textAlign: 'center'}}>
                    No member charities found for this umbrella.
                </Typography>
            )}
            {!loading && charities.length > 0 && (
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5}}>
                    {charities.map(c => (
                        <Box
                            key={c.id}
                            onClick={() => redirect('show', 'charities', c.id)}
                            sx={{
                                p: 1.5,
                                borderRadius: `${t.radius.md}px`,
                                border: `1px solid ${t.color.border.default}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                transition: 'border-color 120ms ease, background-color 120ms ease',
                                '&:hover': {
                                    borderColor: t.color.border.strong,
                                    backgroundColor: t.color.sidebarHover,
                                },
                            }}
                        >
                            <Avatar
                                src={c.logo_name && c.ext ? `${process.env.REACT_APP_CDN_URL}/${c.logo_name}.${c.ext}` : undefined}
                                variant="rounded"
                                sx={{
                                    width: 36, height: 36, fontSize: 14,
                                    bgcolor: t.color.sidebarHover,
                                    color: t.color.text.secondary,
                                    border: `1px solid ${t.color.border.hairline}`,
                                }}
                            >
                                {c.charity_name?.charAt(0)}
                            </Avatar>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                    <StatusDot variant={c.active ? 'success' : 'neutral'}/>
                                    <Typography
                                        sx={{
                                            fontSize: `${t.type.bodyStrong.size}px`,
                                            fontWeight: t.type.bodyStrong.weight,
                                            color: t.color.text.primary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {c.charity_name}
                                    </Typography>
                                </Box>
                                <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, mt: 0.25}}>
                                    {[c.city, c.country].filter(Boolean).join(', ')}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </SectionCard>
    );
};

const EmptyNotice = ({children}) => (
    <Typography
        sx={{
            fontSize: `${t.type.small.size}px`,
            color: t.color.text.muted,
            fontStyle: 'italic',
            py: 0.5,
        }}
    >
        {children}
    </Typography>
);

const SystemInfoTab = ({record}) => {
    const createdAt = formatDateTime(record.created_at ?? record.createdAt);
    const updatedAt = formatDateTime(record.updated_at ?? record.updatedAt);
    const parentId = record.parent_charity_id ?? record.parentCharityId;
    const hasTimestamps = Boolean(createdAt || updatedAt);

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start'}}>
            <SectionCard title="Timestamps">
                {hasTimestamps ? (
                    <>
                        <DataRow label="Created at" value={createdAt} hideWhenEmpty/>
                        <DataRow label="Updated at" value={updatedAt} hideWhenEmpty/>
                    </>
                ) : (
                    <EmptyNotice>
                        Timestamp data is not returned by the API for this record.
                    </EmptyNotice>
                )}
            </SectionCard>
            <SectionCard title="System IDs">
                <DataRow label="Organization ID" value={record.id}/>
                <DataRow label="Parent charity ID" value={parentId}/>
            </SectionCard>
        </Box>
    );
};

const UmbrellaShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const [tab, setTab] = useTabs('details');

    if (!record) return null;

    const logoUrl = record.logo_name && record.ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.logo_name}.${record.ext}`
        : undefined;

    const statusVariant = record.demo_charity ? 'info' : record.active ? 'success' : 'neutral';

    const tabs = [
        {id: 'details', label: translate('Details')},
        {id: 'charities', label: translate('Charities')},
        {id: 'system', label: translate('System Info')},
    ];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: 'Umbrellas', to: '/umbrellas'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant}/>
                        <Box component="span">{record.charity_name}</Box>
                    </Box>
                }
                actions={
                    <>
                        <PrimaryButton
                            variant="primary"
                            startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                            onClick={() => redirect('edit', 'umbrellas', record.id)}
                        >
                            Edit
                        </PrimaryButton>
                    </>
                }
            />

            <StatamicTabs tabs={tabs} value={tab} onChange={setTab}/>

            <Box sx={{p: 3}}>
                {tab === 'details' && <DetailsTab record={record} logoUrl={logoUrl}/>}
                {tab === 'charities' && <CharitiesTab record={record}/>}
                {tab === 'system' && <SystemInfoTab record={record}/>}
            </Box>
        </Box>
    );
};

const UmbrellaShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <UmbrellaShowContent/>
    </Show>
);

export default UmbrellaShowStatamic;
